import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { Button, Input, Text } from '../../components';
import { TAG_SYMBOL_MAPPING_CATEGORIES } from '../AgentForm/const';
import { TAG_SYMBOL_MAPPING_CATEGORY_KEYS } from '../../utilities/tagSymbolMappings';
import { getTagSymbolMappingFieldErrors } from '../AgentForm/utils/validateTagSymbolMappings';
import type {
  TagSymbolMapping,
  TagSymbolMappingCategory,
  TagSymbolMappings,
} from '../../utilities';

export type TagSymbolMappingsListProps = {
  value?: TagSymbolMappings;
  onChange?: (value?: TagSymbolMappings) => void;
  disabled?: boolean;
};

const EMPTY_MAPPING: TagSymbolMapping = { from: '', to: '' };

const getCategoriesWithRows = (
  value?: TagSymbolMappings
): TagSymbolMappingCategory[] =>
  TAG_SYMBOL_MAPPING_CATEGORY_KEYS.filter(
    key => (value?.[key]?.length ?? 0) > 0
  );

export const TagSymbolMappingsList: React.FC<TagSymbolMappingsListProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const mappings = value ?? {};
  const [openCategories, setOpenCategories] = useState<
    Set<TagSymbolMappingCategory>
  >(() => new Set(getCategoriesWithRows(value)));

  useEffect(() => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      getCategoriesWithRows(value).forEach(key => next.add(key));
      return next;
    });
  }, [value]);

  const addCategory = (category: TagSymbolMappingCategory) => {
    setOpenCategories(prev => new Set([...prev, category]));
  };

  const closeCategory = (category: TagSymbolMappingCategory) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      next.delete(category);
      return next;
    });

    if (!(category in mappings)) return;

    const next = { ...mappings };
    delete next[category];
    onChange?.(Object.keys(next).length > 0 ? next : undefined);
  };

  const addRow = (category: TagSymbolMappingCategory) => {
    const current = mappings[category] ?? [];
    onChange?.({ ...mappings, [category]: [...current, { ...EMPTY_MAPPING }] });
  };

  const removeRow = (category: TagSymbolMappingCategory, index: number) => {
    const updated = (mappings[category] ?? []).filter((_, i) => i !== index);
    const next = { ...mappings };

    if (updated.length === 0) {
      delete next[category];
      setOpenCategories(prev => {
        const categories = new Set(prev);
        categories.delete(category);
        return categories;
      });
    } else {
      next[category] = updated;
    }

    onChange?.(Object.keys(next).length > 0 ? next : undefined);
  };

  const updateField = (
    category: TagSymbolMappingCategory,
    index: number,
    field: keyof TagSymbolMapping,
    newValue: string
  ) => {
    const current = mappings[category] ?? [];
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange?.({ ...mappings, [category]: updated });
  };

  const visibleCategories = TAG_SYMBOL_MAPPING_CATEGORIES.filter(({ key }) =>
    openCategories.has(key)
  );

  const availableCategories = TAG_SYMBOL_MAPPING_CATEGORIES.filter(
    ({ key }) => !openCategories.has(key)
  );

  return (
    <div className="w-full flex flex-col gap-3 py-1">
      {visibleCategories.length === 0 && (
        <Text variant="secondary" className="text-small italic">
          No tag symbol mappings configured.
        </Text>
      )}

      {visibleCategories.map(({ key, label, hint }) => {
          const rows = mappings[key] ?? [];

          return (
            <div
              key={key}
              className="border border-gray-200 rounded-xl p-4 flex flex-col gap-4 bg-gray-50"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <Text className="text-medium font-semibold text-mainText">
                    {label}
                  </Text>
                  <Text variant="secondary" className="text-small">
                    {hint}
                  </Text>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!disabled && (
                    <Button
                      size="small"
                      type="outline"
                      icon={<AddIcon fontSize="small" />}
                      disabled={disabled}
                      label="Add mapping"
                      onClick={() => addRow(key)}
                    />
                  )}
                  {!disabled && rows.length === 0 && (
                    <Button
                      type="outline"
                      size="small"
                      tooltip="Remove category"
                      icon={<DeleteIcon fontSize="small" color="error" />}
                      onClick={() => closeCategory(key)}
                      className="px-3 bg-white hover:bg-select"
                    />
                  )}
                </div>
              </div>

              {rows.length === 0 && (
                <Text variant="secondary" className="text-small italic">
                  No mappings yet. Add a mapping to define symbol replacements.
                </Text>
              )}

              {rows.map((item, i) => {
                const fieldErrors = getTagSymbolMappingFieldErrors(item);
                const hasError = !!fieldErrors.from || !!fieldErrors.to;

                return (
                  <div
                    key={i}
                    className={`flex items-stretch gap-2 ${
                      hasError
                        ? 'rounded-xl border border-red-500 bg-red-50 p-2'
                        : ''
                    }`}
                  >
                    <div className="flex-1 flex flex-col gap-2 min-w-0">
                      <div className="flex items-start gap-3">
                        <Text
                          as="span"
                          variant="secondary"
                          className="text-small w-10 shrink-0 pt-2"
                        >
                          From
                        </Text>
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <Input
                            disabled={disabled}
                            value={item.from}
                            onChange={val => updateField(key, i, 'from', val)}
                            defaultSizeClass="w-full"
                          />
                          {!!fieldErrors.from && (
                            <Text variant="error" className="text-small italic">
                              {fieldErrors.from}
                            </Text>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Text
                          as="span"
                          variant="secondary"
                          className="text-small w-10 shrink-0 pt-2"
                        >
                          To
                        </Text>
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <Input
                            disabled={disabled}
                            value={item.to}
                            onChange={val => updateField(key, i, 'to', val)}
                            defaultSizeClass="w-full"
                          />
                          {!!fieldErrors.to && (
                            <Text variant="error" className="text-small italic">
                              {fieldErrors.to}
                            </Text>
                          )}
                        </div>
                      </div>
                    </div>

                    {!disabled && (
                      <Button
                        type="contrast"
                        size="empty"
                        tooltip="Remove mapping"
                        icon={<DeleteIcon fontSize="small" color="error" />}
                        onClick={() => removeRow(key, i)}
                        className="px-3"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

      {!disabled && availableCategories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableCategories.map(({ key, label }) => (
            <Button
              key={key}
              size="small"
              type="outline"
              icon={<AddIcon fontSize="small" />}
              label={label}
              onClick={() => addCategory(key)}
              className="bg-white hover:bg-select"
            />
          ))}
        </div>
      )}
    </div>
  );
};
